/**
 * Nearest-neighbour rotation for the Family C "pixel-to-dot"
 * pipeline.
 *
 * Bilinear sampling would blur source pixels - defeating the
 * "1 pixel = 1 dot" model the renderer is built around. NN
 * aliases at non-90° angles by design; that aliasing IS the dot
 * pattern character Yuko wants. A discrete one-to-one rotation
 * (Ostromoukhov 1994) would be even purer but needs an integer-
 * coordinate rotation table; defer as future polish.
 *
 * Two helpers:
 *   - `rotateImageNN`: forward rotation. Output sized to the
 *     rotated bounding box unless `outDims` overrides.
 *   - `cropToSize`: centre-crop to a smaller rectangle. Used
 *     after the forward rotation to recover original source dims.
 */

export type Rgba = readonly [number, number, number, number];

export const WHITE_RGBA: Rgba = [255, 255, 255, 255];

/**
 * Rotate `src` by `angleDeg` (counter-clockwise positive) using
 * nearest-neighbour sampling. Pixels outside the rotated source
 * are filled with `padColour`.
 *
 * If `outDims` is supplied, the output uses those dimensions
 * (used by the inverse rotation step that maps back into the
 * original source size). Otherwise the output is the smallest
 * rectangle that contains the rotated source's corners.
 *
 * Fast path: `angleDeg === 0 mod 360` returns a structural copy.
 */
export function rotateImageNN(
  src: ImageData,
  angleDeg: number,
  padColour: Rgba = WHITE_RGBA,
  outDims?: { w: number; h: number },
): ImageData {
  const normalised = ((angleDeg % 360) + 360) % 360;
  if (normalised === 0 && !outDims) {
    const copy = new ImageData(src.width, src.height);
    copy.data.set(src.data);
    return copy;
  }

  const rad = (normalised * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  const sw = src.width;
  const sh = src.height;

  // Cardinal angles (90 / 180 / 270) accumulate float dust in
  // cos / sin and the bounding-box ceil rounds up by one. Snap
  // anything within a 1e-9 neighbourhood of an integer before
  // ceiling so the round-trip dimensions are exact.
  const snapCeil = (n: number): number => {
    const rounded = Math.round(n);
    if (Math.abs(n - rounded) < 1e-9) return rounded;
    return Math.ceil(n);
  };
  const dw =
    outDims?.w ?? Math.max(1, snapCeil(Math.abs(sw * cos) + Math.abs(sh * sin)));
  const dh =
    outDims?.h ?? Math.max(1, snapCeil(Math.abs(sw * sin) + Math.abs(sh * cos)));

  const out = new ImageData(dw, dh);
  const dst = out.data;
  const sd = src.data;

  // Source / destination centres. Rotating around centres keeps
  // the visual content centred in the output bounding box.
  const cxs = sw / 2;
  const cys = sh / 2;
  const cxd = dw / 2;
  const cyd = dh / 2;

  // Inverse rotation: for each output pixel, find the source
  // pixel it came from. Equivalent to rotating destination by
  // `-angleDeg` and shifting back to source coords.
  for (let yd = 0; yd < dh; yd++) {
    const cdy = yd + 0.5 - cyd;
    for (let xd = 0; xd < dw; xd++) {
      const cdx = xd + 0.5 - cxd;
      // Inverse rotation = rotate by -angle (cos stays, sin flips).
      const xs = cxs + cdx * cos + cdy * sin;
      const ys = cys - cdx * sin + cdy * cos;

      const ix = Math.floor(xs);
      const iy = Math.floor(ys);
      const di = (yd * dw + xd) * 4;
      if (ix >= 0 && ix < sw && iy >= 0 && iy < sh) {
        const si = (iy * sw + ix) * 4;
        dst[di] = sd[si] ?? padColour[0];
        dst[di + 1] = sd[si + 1] ?? padColour[1];
        dst[di + 2] = sd[si + 2] ?? padColour[2];
        dst[di + 3] = sd[si + 3] ?? padColour[3];
      } else {
        dst[di] = padColour[0];
        dst[di + 1] = padColour[1];
        dst[di + 2] = padColour[2];
        dst[di + 3] = padColour[3];
      }
    }
  }

  return out;
}

/**
 * Centre-crop `src` to a (`w`, `h`) rectangle. Used after the
 * inverse rotation to recover the original source dimensions.
 *
 * Fast path: same dimensions returns a structural copy.
 */
export function cropToSize(src: ImageData, w: number, h: number): ImageData {
  if (src.width === w && src.height === h) {
    const copy = new ImageData(w, h);
    copy.data.set(src.data);
    return copy;
  }
  const out = new ImageData(w, h);
  const dst = out.data;
  const sd = src.data;
  const sw = src.width;
  const sh = src.height;
  // Round towards zero so even-pixel centred crops round
  // consistently with the rotation centre choice.
  const offX = Math.floor((sw - w) / 2);
  const offY = Math.floor((sh - h) / 2);
  for (let y = 0; y < h; y++) {
    const sy = y + offY;
    for (let x = 0; x < w; x++) {
      const sx = x + offX;
      const di = (y * w + x) * 4;
      if (sx >= 0 && sx < sw && sy >= 0 && sy < sh) {
        const si = (sy * sw + sx) * 4;
        dst[di] = sd[si] ?? 255;
        dst[di + 1] = sd[si + 1] ?? 255;
        dst[di + 2] = sd[si + 2] ?? 255;
        dst[di + 3] = sd[si + 3] ?? 255;
      } else {
        dst[di] = 255;
        dst[di + 1] = 255;
        dst[di + 2] = 255;
        dst[di + 3] = 255;
      }
    }
  }
  return out;
}
